// Tranzila iframe integration
// https://docs.tranzila.com/docs/payments-billing/795m2yi7q4nmq-iframe-integration

// Simple iframe integration with return URLs (no webhooks needed)

import {
  TRANZILA_CONFIG,
  PAYMENT_URLS,
  TRANZILA_TEST_CONFIG,
} from "../config/tranzila";

export interface TranzilaPaymentRequest {
  sum: number;
  currency: string;
  description: string;
  orderId: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  returnUrl: string;
  cancelUrl: string;
  // Server-to-server notification endpoint (Tranzila notify_url_address).
  // Optional so existing callers keep working, but should always be set.
  notifyUrl?: string;
}

export interface TranzilaPaymentResponse {
  success: boolean;
  iframeUrl?: string;
  transactionId?: string;
  handshakeToken?: string;
  error?: string;
}

export interface TranzilaHandshakeResponse {
  success: boolean;
  thtk?: string;
  error?: string;
}

export interface TranzilaPaymentStatus {
  transactionId: string;
  status: "pending" | "approved" | "declined" | "cancelled";
  amount: number;
  currency: string;
  description: string;
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  orderId: string;
  timestamp: string;
}

export interface TranzilaIframeConfig {
  supplier: string;
  lang: string;
  currency: string;
  sum: number;
  description: string;
  order_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  return_url: string;
  cancel_url: string;
  hide_credit: boolean;
  hide_myacount: boolean;
  hide_contactinfo: boolean;
  hide_cc: boolean;
  hide_billing: boolean;
  hide_shipping: boolean;
  hide_notes: boolean;
  hide_currency: boolean;
  hide_lang: boolean;
  hide_sum: boolean;
  hide_description: boolean;
  hide_order_id: boolean;
  hide_customer_name: boolean;
  hide_customer_email: boolean;
  hide_customer_phone: boolean;
  hide_return_url: boolean;
  hide_cancel_url: boolean;
}

class TranzilaService {
  private readonly baseURL = TRANZILA_CONFIG.iframeBaseURL;
  private readonly supplier = TRANZILA_CONFIG.supplier;
  private readonly defaultLang = TRANZILA_CONFIG.defaultLang;
  private readonly defaultCurrency = TRANZILA_CONFIG.defaultCurrency;
  private readonly hiddenFields = TRANZILA_CONFIG.hiddenFields;

  /**
   * Validates supplier ID
   */
  private validateSupplierId(): boolean {
    if (
      !this.supplier ||
      this.supplier === "your_supplier_id" ||
      this.supplier === "test_supplier"
    ) {
            return false;
    }
    return true;
  }

  /**
   * Creates a payment iframe URL for Tranzila
   */
  createPaymentIframe(
    paymentData: TranzilaPaymentRequest,
    handshakeToken?: string,
    language?: string
  ): string {
    // Validate supplier ID first
    if (!this.validateSupplierId()) {
      throw new Error(
        "Invalid supplier ID. Please configure a valid Tranzila supplier ID."
      );
    }
    // Determine language code based on current language
    const langCode = language === "ru" ? "ru" : "il"; // ru for Russian, il for Hebrew

    const params = new URLSearchParams({
      // Basic payment parameters according to Tranzila documentation
      sum: paymentData.sum.toString(),
      currency: "1", // 1 for ILS according to documentation
      cred_type: "1", // 1 for regular charge
      lang: langCode, // Dynamic language based on current language

      // Customer information (predetermined additional fields)
      company: paymentData.customerName,
      contact: paymentData.customerName,
      email: paymentData.customerEmail,
      phone: paymentData.customerPhone,

      // Order information
      order_id: paymentData.orderId,
      pdesc: paymentData.description, // Basic Product description for invoice

      // Return URLs (correct parameter names)
      success_url_address: paymentData.returnUrl,
      fail_url_address: paymentData.cancelUrl,

      // Server-to-server notification (sent by Tranzila directly to our backend,
      // independent of the customer's browser). This guarantees the payment is
      // recorded even when Safari blocks the in-iframe success redirect.
      ...(paymentData.notifyUrl
        ? { notify_url_address: paymentData.notifyUrl }
        : {}),

      // UI customization
      trBgColor: "FFFFFF",
      trTextColor: "000000",
      trButtonColor: "FF0000",
      buttonLabel: langCode === "ru" ? "Оплатить" : "שלם", // Payment button text based on language
      nologo: "1", // Hide Tranzila logo

      // Handshake parameters (required for fraud prevention)
      new_process: "1", // Required for handshake verification
    });

    // Add handshake token if provided
    if (handshakeToken) {
      params.append("thtk", handshakeToken);
    }

    // Optional: Add Google Pay support
    // google_pay: "1", // Uncomment to enable Google Pay

    const iframeUrl = `${this.baseURL}?${params.toString()}`;
        return iframeUrl;
  }

  /**
   * Performs handshake with Tranzila API via Firebase Functions
   * According to documentation: https://docs.tranzila.com/docs/payments-billing/795m2yi7q4nmq-iframe-integration#handshake
   */
  async performHandshake(sum: number): Promise<TranzilaHandshakeResponse> {
    try {
      // Get Firebase Functions URL
      const functionsUrl = this.getFunctionsUrl();
      const handshakeEndpoint = `${functionsUrl}/tranzilaHandshake`;

      
      const response = await fetch(handshakeEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ sum }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Handshake failed with status: ${response.status}`
        );
      }

      const result = await response.json();
      
      if (!result.success || !result.thtk) {
        throw new Error(
          result.error || "No thtk token received from handshake"
        );
      }

      return {
        success: true,
        thtk: result.thtk,
      };
    } catch (error) {
            return {
        success: false,
        error:
          error instanceof Error ? error.message : "שגיאה בביצוע handshake",
      };
    }
  }

  /**
   * Gets the Firebase Functions URL
   */
  private getFunctionsUrl(): string {
    // Prefer environment variable, fallback to current project functions URL
    const fromEnv = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL;
    if (fromEnv && fromEnv.trim().length > 0) {
      return fromEnv;
    }
    return "https://us-central1-shmuelweb-475f1.cloudfunctions.net";
  }

  /**
   * Creates a payment and returns iframe configuration
   */
  async createPayment(
    paymentData: TranzilaPaymentRequest,
    language?: string
  ): Promise<TranzilaPaymentResponse> {
    try {
      // Validate payment data
      const validation = this.validatePaymentData(paymentData);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.errors.join(", "),
        };
      }

      // Perform handshake first
      const handshakeResult = await this.performHandshake(paymentData.sum);
      if (!handshakeResult.success || !handshakeResult.thtk) {
        return {
          success: false,
          error: handshakeResult.error || "שגיאה בביצוע handshake",
        };
      }

      // Generate transaction ID
      const transactionId = this.generateTransactionId();

      // Create iframe URL with handshake token and language
      const iframeUrl = this.createPaymentIframe(
        paymentData,
        handshakeResult.thtk,
        language
      );

      return {
        success: true,
        iframeUrl,
        transactionId,
        handshakeToken: handshakeResult.thtk,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "שגיאה לא ידועה",
      };
    }
  }

  /**
   * Generates a unique transaction ID
   */
  private generateTransactionId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `TXN_${timestamp}_${random}`.toUpperCase();
  }

  /**
   * Generates a unique order ID
   */
  generateOrderId(): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    return `ORDER_${timestamp}_${random}`.toUpperCase();
  }

  /**
   * Validates payment data
   */
  validatePaymentData(paymentData: TranzilaPaymentRequest): {
    valid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (!paymentData.sum || paymentData.sum <= 0) {
      errors.push("סכום התשלום חייב להיות גדול מ-0");
    }

    if (!paymentData.description) {
      errors.push("תיאור התשלום הוא שדה חובה");
    }

    if (!paymentData.customerName) {
      errors.push("שם הלקוח הוא שדה חובה");
    }

    if (!paymentData.customerEmail) {
      errors.push("כתובת אימייל היא שדה חובה");
    }

    if (!paymentData.customerPhone) {
      errors.push("מספר טלפון הוא שדה חובה");
    }

    if (!paymentData.returnUrl) {
      errors.push("כתובת החזרה היא שדה חובה");
    }

    if (!paymentData.cancelUrl) {
      errors.push("כתובת ביטול היא שדה חובה");
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Check payment status from return URL parameters
   */
  async checkPaymentStatus(
    orderId: string
  ): Promise<TranzilaPaymentStatus | null> {
    try {
      
      // Payment status is determined by return URL parameters
      // This method is kept for compatibility but not used in iframe flow
      return {
        transactionId: `TXN_${Date.now()}`,
        status: "pending",
        amount: 0,
        currency: "ILS",
        description: `Payment for order ${orderId}`,
        customerName: "Customer",
        customerEmail: "customer@example.com",
        customerPhone: "",
        orderId: orderId,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
            return null;
    }
  }

  /**
   * Cancel payment (implement based on your backend)
   */
  async cancelPayment(transactionId: string): Promise<boolean> {
    try {
      // This would typically call your backend to cancel the payment
      // For now, we'll just log the cancellation request
      
      // In production, you would:
      // 1. Call your backend API
      // 2. Backend calls Tranzila API to cancel
      // 3. Update payment status in database

      return true; // Placeholder return
    } catch (error) {
            return false;
    }
  }

  /**
   * Get iframe dimensions for responsive design
   */
  getIframeDimensions() {
    return TRANZILA_CONFIG.iframeDimensions;
  }

  /**
   * Get iframe styling options
   */
  getIframeStyles() {
    return TRANZILA_CONFIG.iframeStyles;
  }

  /**
   * Generate payment return URLs using configuration
   */
  generatePaymentUrls(registrationId: string) {
    return {
      success: PAYMENT_URLS.success(registrationId),
      cancelled: PAYMENT_URLS.cancelled(registrationId),
      failed: PAYMENT_URLS.failed(registrationId),
    };
  }

  /**
   * Get test configuration for development
   */
  getTestConfig() {
    return TRANZILA_TEST_CONFIG;
  }

  /**
   * Create test payment data for development
   */
  createTestPaymentData() {
    return {
      sum: TRANZILA_TEST_CONFIG.testAmount,
      currency: TRANZILA_CONFIG.defaultCurrency,
      description: TRANZILA_TEST_CONFIG.testDescription,
      orderId: this.generateOrderId(),
      customerName: "Test Customer",
      customerEmail: "test@example.com",
      customerPhone: "050-1234567",
      returnUrl: PAYMENT_URLS.success("test"),
      cancelUrl: PAYMENT_URLS.cancelled("test"),
    };
  }

  /**
   * Get additional configuration parameters
   */
  getAdditionalParams() {
    return TRANZILA_CONFIG.additionalParams;
  }

  /**
   * Check if running in production environment
   */
  isProduction() {
    return TRANZILA_CONFIG.isProduction;
  }

  /**
   * Check if running in development environment
   */
  isDevelopment() {
    return TRANZILA_CONFIG.isDevelopment;
  }

  /**
   * Get payment status from URL parameters
   * Used when user returns from Tranzila payment
   */
  getPaymentStatusFromUrl(
    urlParams: URLSearchParams
  ): "success" | "cancelled" | "failed" | "pending" {
    const status = urlParams.get("status");
    switch (status) {
      case "success":
        return "success";
      case "cancelled":
        return "cancelled";
      case "failed":
        return "failed";
      default:
        return "pending";
    }
  }
}

export const tranzilaService = new TranzilaService();
